import * as React from 'react';

import Mousetrap from 'mousetrap';

import type {User} from 'nutshell-core/types';

import {SearchBar} from 'shells/navigation/search-bar/search-bar';
import {useDebounce} from 'shells/utils/hooks/use-debounce';

import {SearchBarPopover} from '../popover/search-bar-popover';

interface Props {
    user: User | null | undefined;
    /** When provided, clicking/focusing the search bar opens the command palette modal instead */
    onOpenCommandPalette?: () => void;
}

export function SearchBarContainer(props: Props) {
    const {user, onOpenCommandPalette} = props;
    const [isSearchBarFocused, setIsSearchBarFocused] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    const debouncedSearchValue = useDebounce(searchValue, 500);
    const isTyping = searchValue !== debouncedSearchValue;

    const searchBarRef = React.useRef(null);
    const searchContainerRef = React.useRef(null);

    const isCommandPaletteMode = Boolean(onOpenCommandPalette);

    const focusSearchBar = React.useCallback(
        (e) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            e && e.preventDefault();
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            e && e.stopPropagation();

            // When command palette FF is on, delegate to the modal instead
            if (onOpenCommandPalette) {
                onOpenCommandPalette();

                return;
            }

            // @ts-expect-error
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            searchBarRef.current && searchBarRef.current.focus();
            if (isSearchBarFocused) {
                return;
            }

            setIsSearchBarFocused(true);
        },
        [isSearchBarFocused, onOpenCommandPalette]
    );

    React.useEffect(() => {
        if (user && user.hasDisabledShortcuts) {
            return;
        }

        // When FF is on, '/' opens the command palette modal (same as Cmd+K)
        // When FF is off, both '/' and Cmd+K focus the inline search bar
        const shortcuts = isCommandPaletteMode ? '/' : ['/', 'meta+k', 'ctrl+k'];
        Mousetrap.bind(shortcuts, (e) => {
            e.preventDefault();
            if (onOpenCommandPalette) {
                onOpenCommandPalette();
            } else if (!isSearchBarFocused) {
                // @ts-expect-error
                focusSearchBar();
            }
        });

        return () => {
            if (Array.isArray(shortcuts)) {
                shortcuts.forEach((s) => Mousetrap.unbind(s));
            } else {
                Mousetrap.unbind(shortcuts);
            }
        };
    }, [isSearchBarFocused, focusSearchBar, user, isCommandPaletteMode, onOpenCommandPalette]);

    React.useEffect(() => {
        if (isCommandPaletteMode) return;
        if (searchValue.length > 0 && !isSearchBarFocused) {
            // @ts-expect-error
            focusSearchBar();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, isCommandPaletteMode]);

    return (
        <div
            className='nut-navbar--left-section--search-bar-container'
            onClick={focusSearchBar}
            ref={searchContainerRef}
        >
            {!isCommandPaletteMode && isSearchBarFocused && (
                <div
                    className='overlay-click-to-blur'
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // @ts-expect-error
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        searchBarRef.current && searchBarRef.current.blur();
                        setIsSearchBarFocused(false);
                    }}
                ></div>
            )}
            <div
                data-tip={isSearchBarFocused ? '' : 'Press "/" to search'}
                data-place='bottom'
                data-delay-show={50}
                style={isCommandPaletteMode ? {pointerEvents: 'none'} : undefined}
            >
                <SearchBar
                    hasBorder={true}
                    onChange={isCommandPaletteMode ? () => {} : setSearchValue}
                    value={isCommandPaletteMode ? '' : searchValue}
                    isDark={isCommandPaletteMode || !isSearchBarFocused}
                    searchBarRef={
                        isCommandPaletteMode
                            ? undefined
                            : (c) => {
                                  // @ts-expect-error
                                  searchBarRef.current = c;
                              }
                    }
                    placeholder={isCommandPaletteMode ? 'Search or press ⌘K…' : 'Search…'}
                />
            </div>
            {!isCommandPaletteMode && isSearchBarFocused && (
                <SearchBarPopover
                    user={user}
                    searchBarRef={searchBarRef.current}
                    setSearchValue={setSearchValue}
                    setIsSearchBarFocused={setIsSearchBarFocused}
                    searchValue={debouncedSearchValue}
                    isTyping={isTyping}
                    anchor={searchContainerRef.current}
                    hasTyped={searchValue.length > 0}
                />
            )}
        </div>
    );
}
